import React, { useEffect, useState } from "react";
import { useInstallPrompt } from "../shared/useInstallPrompt";
import { hasFinishedMatch, subscribePlayed } from "../shared/played";

// A gentle, dismissible "install the app" nudge that slides up from the bottom
// once the player has actually finished a match (a high-intent moment), instead
// of a cold button in the nav. Remembers dismissal, and disappears for good once
// the app is installed. On iOS it expands the manual Share -> Add to Home Screen
// hint, since Safari has no programmatic install.

const KEY = "ar_install_nudge_dismissed";

function wasDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

const INSTALL_ICON = (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);
const SHARE_ICON = (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: "-3px", margin: "0 2px" }}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <path d="M16 6l-4-4-4 4" />
    <path d="M12 2v14" />
  </svg>
);

export default function InstallPrompt() {
  const { canInstall, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  const [played, setPlayed] = useState(hasFinishedMatch());
  const [dismissed, setDismissed] = useState(wasDismissed());
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => subscribePlayed(() => setPlayed(hasFinishedMatch())), []);

  const eligible = !isStandalone && !dismissed && played && (canInstall || isIOS);
  if (!eligible) return null;

  function dismiss() {
    setDismissed(true);
    try {
      window.localStorage.setItem(KEY, "1");
    } catch {
      /* storage blocked; hidden for this session regardless */
    }
  }

  async function install() {
    if (isIOS) {
      setIosHint((v) => !v);
      return;
    }
    const outcome = await promptInstall();
    // Accepted or already gone -> stop nudging. A dismissal of the native prompt
    // leaves our pill up so they can try again later.
    if (outcome === "accepted" || outcome === "unavailable") dismiss();
  }

  const C = { accent: "#28c4d4", panel: "#0c1524", line: "rgba(255,255,255,0.12)", txt: "#eaf0ff", muted: "#8995b3" };

  return (
    <div
      role="dialog"
      aria-label="Install Achtung Royale"
      style={{
        position: "fixed", left: "50%", bottom: 22, transform: "translateX(-50%)", zIndex: 2600,
        display: "flex", alignItems: "center", gap: 14, width: "min(94vw, 460px)", boxSizing: "border-box",
        background: C.panel, border: `1px solid ${C.line}`, borderLeft: `4px solid ${C.accent}`,
        borderRadius: 14, padding: "14px 14px 14px 16px",
        boxShadow: `0 18px 46px rgba(0,0,0,0.55), 0 0 30px rgba(40,196,212,0.18)`,
        fontFamily: "Rajdhani, system-ui, sans-serif",
        animation: "ar-install-in 0.4s cubic-bezier(.2,1.2,.35,1)",
      }}
    >
      <style>{"@keyframes ar-install-in{0%{opacity:0;transform:translate(-50%,26px)}100%{opacity:1;transform:translate(-50%,0)}}"}</style>
      <span style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 10, background: "rgba(40,196,212,0.12)", color: C.accent }}>
        {INSTALL_ICON}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: C.txt, fontWeight: 700, fontSize: 16, letterSpacing: 0.2, lineHeight: 1.2 }}>Enjoying it? Install the app</div>
        <div style={{ color: C.muted, fontWeight: 600, fontSize: 13, marginTop: 2 }}>
          {isIOS && iosHint ? (
            <span>Tap {SHARE_ICON} Share, then <strong style={{ color: C.txt }}>Add to Home Screen</strong>.</span>
          ) : (
            "One-tap, fullscreen, no browser bar."
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={install}
        style={{ flex: "0 0 auto", padding: "0 16px", height: 38, borderRadius: 9, border: "none", cursor: "pointer", background: C.accent, color: "#06121a", fontFamily: "Orbitron, sans-serif", fontWeight: 800, fontSize: 12, letterSpacing: 1 }}
      >
        {isIOS ? "HOW" : "INSTALL"}
      </button>
      <button
        type="button"
        onClick={dismiss}
        title="Not now"
        aria-label="Dismiss"
        style={{ flex: "0 0 auto", width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: "rgba(255,255,255,0.06)", color: C.muted, cursor: "pointer", fontSize: 20, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}
